import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  VARIANT_K12_DWB,
  VARIANT_K8_CORE,
} from 'components/SiteUserTrainingGrid/constants'
import { PROGRAM_OPTION_DWB_LABEL, PROGRAM_OPTION_K8_LABEL } from './constants'
import {
  ProgramSelectCaret,
  ProgramSelectCheckWrap,
  ProgramSelectDropdown,
  ProgramSelectOption,
  ProgramSelectOptionInner,
  ProgramSelectOptionLabel,
  ProgramSelectTrigger,
  ProgramVariantSelectRoot,
} from './component.styles'

const OPTIONS = [
  { value: VARIANT_K8_CORE, label: PROGRAM_OPTION_K8_LABEL },
  { value: VARIANT_K12_DWB, label: PROGRAM_OPTION_DWB_LABEL },
]

function CheckIcon() {
  return (
    <svg
      aria-hidden
      height="13"
      viewBox="0 0 17 13"
      width="17"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.59 12.025L0 6.435l1.41-1.41 4.18 4.175L15.59 0 17 1.415 5.59 12.025z"
        fill="currentColor"
      />
    </svg>
  )
}

const ProgramVariantSelect = ({
  'aria-label': ariaLabel,
  dataTestId,
  onChange,
  value,
}) => {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  const selectedLabel =
    OPTIONS.find(o => o.value === value)?.label ?? PROGRAM_OPTION_K8_LABEL

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return undefined

    const onDocMouseDown = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        close()
      }
    }

    const onKeyDown = e => {
      if (e.key === 'Escape') {
        close()
      }
    }

    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, close])

  const choose = optionValue => {
    onChange(optionValue)
    close()
  }

  return (
    <ProgramVariantSelectRoot ref={rootRef}>
      <ProgramSelectTrigger
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        data-testid={dataTestId}
        type="button"
        onClick={() => setOpen(o => !o)}
      >
        <span>{selectedLabel}</span>
        <ProgramSelectCaret aria-hidden $open={open} />
      </ProgramSelectTrigger>
      {open && (
        <ProgramSelectDropdown role="listbox">
          {OPTIONS.map(opt => (
            <ProgramSelectOption
              key={opt.value}
              aria-selected={value === opt.value}
              role="option"
              type="button"
              onClick={() => choose(opt.value)}
            >
              <ProgramSelectOptionInner>
                <ProgramSelectOptionLabel>{opt.label}</ProgramSelectOptionLabel>
                {value === opt.value ? (
                  <ProgramSelectCheckWrap aria-hidden>
                    <CheckIcon />
                  </ProgramSelectCheckWrap>
                ) : null}
              </ProgramSelectOptionInner>
            </ProgramSelectOption>
          ))}
        </ProgramSelectDropdown>
      )}
    </ProgramVariantSelectRoot>
  )
}

ProgramVariantSelect.propTypes = {
  'aria-label': PropTypes.string,
  dataTestId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOf([VARIANT_K8_CORE, VARIANT_K12_DWB]).isRequired,
}

export default ProgramVariantSelect
